import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Plus, Save } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi, ApiError, type ModelPricingFull, type PricingPreview } from '@/lib/api';
import { PricingCalculator, type PricingSuggestion } from './PricingCalculator';

export function PricingPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['pricing'], queryFn: adminApi.listPricing });
  const [showNew, setShowNew] = useState(false);
  const [prefill, setPrefill] = useState<PricingSuggestion | null>(null);
  const [prefillKey, setPrefillKey] = useState(0);

  const handleFillNew = (values: PricingSuggestion) => {
    setPrefill(values);
    setPrefillKey((k) => k + 1);
    setShowNew(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight">模型定价</h2>
        <Button onClick={() => setShowNew((v) => !v)}>
          <Plus className="h-4 w-4 mr-1" />
          新增模型
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        每个模型记录 3 个字段：<code className="text-xs bg-muted px-1 rounded">USD / 1M tokens</code>（上游成本）。
        扣费按固定规则 <code className="text-xs bg-muted px-1 rounded">1 credit = $0.0001</code> 自动换算，
        不需要额外配置 credit 系数。改动会自动写入 pricing history。
      </p>

      <PricingCalculator onFillNew={handleFillNew} />

      {showNew && (
        <NewPricingForm
          key={prefillKey}
          initialValues={prefill}
          onDone={() => {
            setShowNew(false);
            setPrefill(null);
            queryClient.invalidateQueries({ queryKey: ['pricing'] });
          }}
          onCancel={() => {
            setShowNew(false);
            setPrefill(null);
          }}
        />
      )}

      {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
      {data && (
        <div className="space-y-4">
          {data.map((p) => (
            <PricingCard key={p.id} row={p} onSave={() => queryClient.invalidateQueries({ queryKey: ['pricing'] })} />
          ))}
        </div>
      )}
    </div>
  );
}

function PricingCard({ row, onSave }: { row: ModelPricingFull; onSave: () => void }) {
  const [edit, setEdit] = useState(false);
  const [usdInUnc, setUsdInUnc] = useState(String(row.cost_usd_per_1m_input_uncached));
  const [usdInC, setUsdInC] = useState(String(row.cost_usd_per_1m_input_cached));
  const [usdOut, setUsdOut] = useState(String(row.cost_usd_per_1m_output));
  const [active, setActive] = useState(row.is_active);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [preview, setPreview] = useState<PricingPreview | null>(null);

  const buildPatch = (): Record<string, unknown> => {
    const patch: Record<string, unknown> = {};
    if (usdInUnc !== String(row.cost_usd_per_1m_input_uncached)) {
      patch.cost_usd_per_1m_input_uncached = Number(usdInUnc);
    }
    if (usdInC !== String(row.cost_usd_per_1m_input_cached)) {
      patch.cost_usd_per_1m_input_cached = Number(usdInC);
    }
    if (usdOut !== String(row.cost_usd_per_1m_output)) {
      patch.cost_usd_per_1m_output = Number(usdOut);
    }
    if (active !== row.is_active) patch.is_active = active;
    return patch;
  };

  const save = useMutation({
    mutationFn: () => adminApi.updatePricing(row.model, buildPatch()),
    onSuccess: () => {
      setFeedback('已保存');
      setEdit(false);
      setPreview(null);
      onSave();
    },
    onError: (e) => setFeedback(e instanceof ApiError ? e.message : String(e)),
  });

  const previewMut = useMutation({
    mutationFn: () => adminApi.previewPricing(row.model, { ...buildPatch(), days: 30 }),
    onSuccess: (r) => setPreview(r),
    onError: (e) => setFeedback(e instanceof ApiError ? e.message : String(e)),
  });

  const dirty = Object.keys(buildPatch()).length > 0;

  // Credits implied by current USD (read-only, derived via 1 credit = $0.0001):
  const creditsFromUsd = (usd: number) => Math.round(usd * 10_000);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <span className="font-mono">{row.model}</span>
          <span
            className={`text-xs px-2 py-0.5 rounded ${
              row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {row.is_active ? 'active' : 'disabled'}
          </span>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setEdit((v) => !v)}>
            {edit ? '收起' : '编辑'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!edit && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <Stat
              label="USD input uncached / 1M"
              value={`$${row.cost_usd_per_1m_input_uncached} (${creditsFromUsd(row.cost_usd_per_1m_input_uncached).toLocaleString()} credits)`}
            />
            <Stat
              label="USD input cached / 1M"
              value={`$${row.cost_usd_per_1m_input_cached} (${creditsFromUsd(row.cost_usd_per_1m_input_cached).toLocaleString()} credits)`}
            />
            <Stat
              label="USD output / 1M"
              value={`$${row.cost_usd_per_1m_output} (${creditsFromUsd(row.cost_usd_per_1m_output).toLocaleString()} credits)`}
            />
          </div>
        )}

        {edit && (
          <>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="USD input uncached / 1M">
                <Input type="number" step="0.000001" value={usdInUnc} onChange={(e) => setUsdInUnc(e.target.value)} />
              </Field>
              <Field label="USD input cached / 1M">
                <Input type="number" step="0.000001" value={usdInC} onChange={(e) => setUsdInC(e.target.value)} />
              </Field>
              <Field label="USD output / 1M">
                <Input type="number" step="0.000001" value={usdOut} onChange={(e) => setUsdOut(e.target.value)} />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={active} onCheckedChange={(v) => setActive(v === true)} />
              启用
            </label>
            <div className="flex gap-2 items-center pt-2">
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                <Save className="h-4 w-4 mr-1" />
                {save.isPending ? LOADING_LABELS.saving : '保存'}
              </Button>
              <Button variant="outline" onClick={() => previewMut.mutate()} disabled={!dirty || previewMut.isPending}>
                <Eye className="h-4 w-4 mr-1" />
                {previewMut.isPending ? LOADING_LABELS.computing : '预览影响 (30 天)'}
              </Button>
              {feedback && <span className="text-xs text-muted-foreground">{feedback}</span>}
            </div>

            {preview && (
              <div className="p-3 bg-muted rounded-md text-sm space-y-1">
                <p className="font-medium">改价影响（按过去 {preview.days} 天数据回算）</p>
                <p>
                  事件数 <b>{preview.n_events}</b> · 受影响用户 <b>{preview.n_users_affected}</b>
                </p>
                <p>
                  credit 总量 {preview.credits_old_total.toLocaleString()} → <b>{preview.credits_new_total.toLocaleString()}</b>
                </p>
                <p>
                  变动{' '}
                  <b className={preview.credits_delta > 0 ? 'text-red-600' : 'text-green-600'}>
                    {preview.credits_delta > 0 ? '+' : ''}
                    {preview.credits_delta.toLocaleString()}
                    {preview.credits_delta_pct !== null &&
                      ` (${preview.credits_delta_pct > 0 ? '+' : ''}${preview.credits_delta_pct}%)`}
                  </b>
                </p>
                {preview.credits_delta_pct !== null && Math.abs(preview.credits_delta_pct) > 30 && (
                  <p className="text-orange-600">⚠️ 变动超过 30%，会显著影响用户感知</p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function NewPricingForm({
  onDone,
  onCancel,
  initialValues,
}: {
  onDone: () => void;
  onCancel: () => void;
  initialValues?: PricingSuggestion | null;
}) {
  const [model, setModel] = useState('');
  const [usdInUnc, setUsdInUnc] = useState(
    initialValues?.cost_usd_per_1m_input_uncached?.toString() ?? '',
  );
  const [usdInC, setUsdInC] = useState(
    initialValues?.cost_usd_per_1m_input_cached?.toString() ?? '',
  );
  const [usdOut, setUsdOut] = useState(initialValues?.cost_usd_per_1m_output?.toString() ?? '');

  const create = useMutation({
    mutationFn: () =>
      adminApi.createPricing({
        model: model.trim(),
        cost_usd_per_1m_input_uncached: Number(usdInUnc),
        cost_usd_per_1m_input_cached: Number(usdInC),
        cost_usd_per_1m_output: Number(usdOut),
      }),
    onSuccess: onDone,
  });

  const errMsg = create.error instanceof ApiError ? create.error.message : create.error ? String(create.error) : null;

  const canSubmit =
    model.trim().length > 0 &&
    Number.isFinite(Number(usdInUnc)) &&
    Number.isFinite(Number(usdInC)) &&
    Number.isFinite(Number(usdOut)) &&
    Number(usdInUnc) >= 0 &&
    Number(usdInC) >= 0 &&
    Number(usdOut) >= 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">新增模型定价</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="model (如 moonshotai/Kimi-K2.5)">
            <Input value={model} onChange={(e) => setModel(e.target.value)} />
          </Field>
          <Field label="USD input uncached / 1M">
            <Input type="number" step="0.000001" value={usdInUnc} onChange={(e) => setUsdInUnc(e.target.value)} />
          </Field>
          <Field label="USD input cached / 1M">
            <Input type="number" step="0.000001" value={usdInC} onChange={(e) => setUsdInC(e.target.value)} />
          </Field>
          <Field label="USD output / 1M">
            <Input type="number" step="0.000001" value={usdOut} onChange={(e) => setUsdOut(e.target.value)} />
          </Field>
        </div>
        {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
        <div className="flex gap-2">
          <Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending}>
            {create.isPending ? LOADING_LABELS.creating : '创建'}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            取消
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-mono">{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
